// In Next.js, this file would be called: app/providers.jsx
"use client"

import { ThemeProvider } from "@/components/theme-provider"
import { Toaster } from "sonner"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"

// We can not useState or useRef in a server component, which is why we are
// extracting this part out into it's own file with 'use client' on top
import { PropsWithChildren, useEffect, useState } from "react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { baseUrl, defaultQueryFn } from "@/api/default-query"
import { TooltipProvider } from "@/components/ui/tooltip"
import { AppProgressBar as ProgressBar } from "next-nprogress-bar"
import { Provider } from "jotai"
import { NuqsAdapter } from "nuqs/adapters/next/app"
import { UpgradeSubscription } from "@/components/upgrade-subscription"

import posthog from "posthog-js"
import { PostHogProvider } from "posthog-js/react"
import { useLogout } from "@/api"
import { ScreenSizeContext } from "@/components/screen.size.context"
import { useMediaQuery } from "@/hooks/use-media-query"

if (typeof window !== "undefined") {
  posthog.init(
    process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "phc_pAfRkta2U5zhpNjqNevjnonN5TbssAy5lrybpySWFRL",
    {
      api_host: "/ingest",
      ui_host: "https://eu.posthog.com",
    },
  )
}

export function CSPostHogProvider({ children }: { children: React.ReactNode }) {
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}

function SessionRefresher() {
  const logoutMutation = useLogout()

  useEffect(() => {
    const validateSession = async () => {
      try {
        const response = await fetch(`${baseUrl}/users/refreshSession`, {
          method: "GET",
          credentials: "include",
        })
        if (response.status === 401) {
          const logoutMutation = useLogout()
          logoutMutation.mutate()
        }
      } catch (e) {
        logoutMutation.mutate()
      }
    }

    validateSession()
  }, [])

  return null
}

export default function Providers({ children }: PropsWithChildren<object>) {
  const isMobile = useMediaQuery("(max-width: 768px)")
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000 /** 1minute */ * 5,
            queryFn: defaultQueryFn,
            retry: false,
          },
        },
      }),
  )

  return (
    <Provider>
      <ScreenSizeContext.Provider value={{ isMobile }}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider attribute="class" enableSystem>
            <NuqsAdapter>
              <TooltipProvider delayDuration={400}>
                <SessionRefresher />
                {children}
              </TooltipProvider>
            </NuqsAdapter>
            <Toaster richColors />
            <UpgradeSubscription />
          </ThemeProvider>
          <ProgressBar
            height="4px"
            color="#FBABFF"
            options={{ showSpinner: false }}
            shallowRouting
          />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ScreenSizeContext.Provider>
    </Provider>
  )
}
